/**
 * Keeps the last line of a paragraph from holding only one or two words (and
 * of a heading or list item too long for the browser to balance): the spaces
 * between its last three words become no-break spaces, so those words always
 * wrap onto the last line together. A hyphen
 * inside that group ("condition-focused") gets a word joiner after it, so the
 * line cannot break there either (Poppins has no no-break hyphen glyph).
 *
 * CSS alone cannot do this — text-wrap: pretty (index.css) only rules out a
 * single word, and only in Chrome — so unlike brandColors this does edit the
 * text. The edits are made inside the existing text nodes, so the nodes React
 * and Google Translate own stay the same nodes; when either rewrites one, the
 * observer binds it again. Already-bound text is left as it is, so the
 * observer settles after one pass.
 *
 * A bound group is measured and kept narrower than its block, so it fits a
 * line; should the block later narrow (a phone turned upright), index.css
 * lets the group break rather than overflow. Text too short to wrap (labels,
 * buttons) and text clamped to a few lines, whose last line is hidden
 * anyway, are left alone.
 */
// headings and list items only: paragraph text fills its lines, and the
// stylesheet's text-wrap: pretty keeps a lone word off the last line (client, 2026-10-31)
const BLOCKS = "li, h1, h2, h3, h4, h5, h6";
// (text inside [data-free-wrap] fills each line instead, as the client asked for the video articles)
const SKIP = "script, style, textarea, input, select, option, pre, code, [contenteditable], [data-free-wrap]";
const NBSP = String.fromCharCode(0xa0);
const WJ = String.fromCharCode(0x2060); // word joiner: no break on either side, never drawn
const KEEP = 3; // words that stay together on the last line
const MIN_WORDS = 5; // shorter text is left alone
const FIT = 0.9; // a group's widest share of its block

type Edit = { node: Text; at: number; len: number; text: string };
type Word = { node: Text; start: number; endNode: Text; end: number };

/** the element whose box a text node's line belongs to: the nearest non-inline ancestor up to the block */
function boxOf(node: Text, block: Element): Element {
  for (let el = node.parentElement; el && el !== block; el = el.parentElement) {
    const d = getComputedStyle(el).display;
    if (!d.startsWith("inline") && d !== "contents") return el;
  }
  return block;
}

/** the edits that keep the block's last words together (none if already made) */
function editsFor(block: HTMLElement): Edit[] {
  const width = block.clientWidth;
  if (!width) return [];
  const style = getComputedStyle(block);
  if (style.getPropertyValue("-webkit-line-clamp") !== "none") return [];
  // headings and list items wrap into even lines (index.css), which already
  // leaves no short last line; binding words there would only upset the
  // balance. Chrome balances up to six lines, so longer ones are still bound.
  if (/balance/.test(style.getPropertyValue("text-wrap") + style.getPropertyValue("text-wrap-style"))) {
    const line = parseFloat(style.lineHeight) || (parseFloat(style.fontSize) || 16) * 1.2;
    if (block.clientHeight / line <= 6.5) return [];
  }
  const text = block.textContent ?? "";
  if (text.trim().split(/\s+/).length < MIN_WORDS) return [];

  const nodes: Text[] = [];
  const walk = document.createTreeWalker(block, NodeFilter.SHOW_TEXT);
  for (let n = walk.nextNode(); n; n = walk.nextNode()) {
    if (n.parentElement?.closest(BLOCKS) === block) nodes.push(n as Text);
  }

  // read backwards: the last words, and the whitespace run before each of
  // them (gaps[0] sits before the last word)
  const words: Word[] = [];
  const gaps: Edit[] = [];
  let inWord = false;
  let box: Element | null = null;
  scan: for (let i = nodes.length - 1; i >= 0; i--) {
    const node = nodes[i];
    const b = boxOf(node, block);
    if (box && b !== box) break; // a different line box: not the same run of text
    box = b;
    const t = node.data;
    for (let k = t.length - 1; k >= 0; k--) {
      if (!/\s/.test(t[k])) {
        if (!inWord) {
          if (words.length === KEEP) break scan;
          words.push({ node, start: k, endNode: node, end: k + 1 });
          inWord = true;
        }
        const w = words[words.length - 1];
        w.node = node;
        w.start = k;
      } else if (inWord) {
        inWord = false;
        if (words.length < KEEP) gaps.push({ node, at: k, len: 1, text: NBSP });
      } else {
        // the rest of a run already recorded
        const g = gaps[gaps.length - 1];
        if (g && g.node === node && g.at === k + 1) {
          g.at = k;
          g.len++;
        }
      }
    }
  }
  if (words.length < KEEP) return [];

  // as many gaps as fit: a group wider than the line could hold would overflow
  const limit = width * FIT;
  const space = (parseFloat(style.fontSize) || 16) * 0.3;
  const range = document.createRange();
  const widths = words.map((w) => {
    range.setStart(w.node, w.start);
    range.setEnd(w.endNode, w.end);
    // a word broken at a hyphen has a piece on each line: add the pieces
    return [...range.getClientRects()].reduce((a, r) => a + r.width, 0);
  });
  let take = gaps.length;
  while (take > 0 && widths.slice(0, take + 1).reduce((a, b) => a + b, 0) + take * space > limit) take--;

  const edits = gaps.slice(0, take).filter((g) => !(g.len === 1 && g.node.data[g.at] === NBSP));
  // the group's words after its first may not break at a hyphen
  for (const w of words.slice(0, take)) {
    if (w.node !== w.endNode) continue;
    for (let k = w.start + 1; k < w.end - 1; k++) {
      if (w.node.data[k] === "-" && w.node.data[k + 1] !== WJ) edits.push({ node: w.node, at: k + 1, len: 0, text: WJ });
    }
  }
  return edits;
}

/** when the browser is idle (soon after at the latest), so this never holds up the page;
 *  the work is told how long it may run (null: no idle callbacks, so a short slice) */
const whenIdle = (fn: (deadline: IdleDeadline | null) => void) => {
  // Safari before 18 has no requestIdleCallback
  if (typeof window.requestIdleCallback === "function") window.requestIdleCallback(fn, { timeout: 1000 });
  else setTimeout(() => fn(null), 200);
};
/** blocks one slice may measure when idle time has run out, so no slice is a long task */
const SLICE = 40;

export function startNoOrphans() {
  // the blocks to look at: the whole page at first, then only those that
  // changed (a slider turning, a route, an accordion), so a page is never
  // measured whole again for one changed line
  let everything = true;
  const changed = new Set<HTMLElement>();
  const queueIn = (el: Element) => {
    if (el.matches(BLOCKS)) changed.add(el as HTMLElement);
    el.querySelectorAll<HTMLElement>(BLOCKS).forEach((b) => changed.add(b));
  };

  const run = (deadline: IdleDeadline | null) => {
    if (everything) document.querySelectorAll<HTMLElement>(BLOCKS).forEach((b) => changed.add(b));
    everything = false;
    // read blocks first, then write, so the layout is worked out once: as
    // many as the idle time allows (a slice of SLICE without it), the rest at
    // the next idle moment
    const byNode = new Map<Text, Edit[]>();
    let done = 0;
    for (const block of changed) {
      if (done >= SLICE && !(deadline && !deadline.didTimeout && deadline.timeRemaining() > 4)) break;
      changed.delete(block);
      done++;
      if (!block.isConnected || block.querySelector(BLOCKS) || block.closest(SKIP)) continue;
      for (const e of editsFor(block)) byNode.set(e.node, [...(byNode.get(e.node) ?? []), e]);
    }
    // within a node, the last edit first, so none shifts one still to come
    for (const [node, edits] of byNode) {
      for (const e of edits.sort((a, b) => b.at - a.at)) node.replaceData(e.at, e.len, e.text);
    }
    if (changed.size) later();
  };

  let pending = false;
  const later = () => {
    if (pending) return;
    pending = true;
    whenIdle((deadline) => { pending = false; run(deadline); });
  };
  new MutationObserver((records) => {
    for (const r of records) {
      const el = r.target.nodeType === Node.ELEMENT_NODE ? (r.target as Element) : r.target.parentElement;
      const block = el?.closest<HTMLElement>(BLOCKS);
      if (block) changed.add(block);
      r.addedNodes.forEach((n) => { if (n.nodeType === Node.ELEMENT_NODE) queueIn(n as Element); });
    }
    if (changed.size) later();
  }).observe(document.body, { childList: true, subtree: true, characterData: true });
  // <details> opening shows text without a DOM change
  document.addEventListener("toggle", (e) => { if (e.target instanceof Element) queueIn(e.target); later(); }, true);
  later();
}
